"use client";

import { GoogleAnalytics, GoogleTagManager } from "@next/third-parties/google";
import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { posthogPageview } from "@/lib/posthog";

export const CONSENT_EVENT = "cookie-consent-changed";

/** SPA route geçişlerinde tüm piksellere sayfa görüntüleme gönderir.
 *  İlk yükleme GA4 config / Metrica init ile zaten sayılır — atlanır. */
function RouteTracker({ gaId, yandexId }: { gaId?: string; yandexId?: string }) {
  const pathname = usePathname();
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const url = `${pathname}${window.location.search}`;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "page_view", page_path: url });
    } catch {}
    if (gaId && typeof window.gtag === "function") {
      try {
        window.gtag("event", "page_view", { page_path: url });
      } catch {}
    }
    if (yandexId && typeof window.ym === "function") {
      try {
        window.ym(Number(yandexId), "hit", url);
      } catch {}
    }
    if (typeof window.fbq === "function") {
      try {
        window.fbq("track", "PageView");
      } catch {}
    }
    try {
      window.ttq?.page?.();
    } catch {}
    posthogPageview(url);
  }, [pathname, gaId, yandexId]);

  return null;
}

export function Analytics() {
  const gaId = process.env.NEXT_PUBLIC_GA_ID;
  const metaId = process.env.NEXT_PUBLIC_META_PIXEL_ID;
  const tiktokId = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID;
  const yandexId = process.env.NEXT_PUBLIC_YANDEX_METRICA_ID;
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
  const [consent, setConsent] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const check = () => {
      let next = { analytics: false, marketing: false };
      try {
        const raw = localStorage.getItem("onomac-cookie-consent");
        if (raw === "accepted") next = { analytics: true, marketing: true };
        else if (raw !== "rejected") {
          const parsed = raw ? JSON.parse(raw) : null;
          if (parsed?.status === "accepted") next = { analytics: true, marketing: true };
          else if (parsed?.status === "custom") next = { analytics: !!parsed.analytics, marketing: !!parsed.marketing };
        }
      } catch {
        // next varsayılan (reddedilmiş) kalır
      }
      // Olay fırtınasında aynı değerle yeniden render tetiklenmesin
      setConsent((prev) =>
        prev.analytics === next.analytics && prev.marketing === next.marketing ? prev : next
      );
    };
    check();
    // Polling yok — CookieConsent custom event + cross-tab storage dinlenir
    window.addEventListener(CONSENT_EVENT, check);
    window.addEventListener("storage", check);
    return () => {
      window.removeEventListener(CONSENT_EVENT, check);
      window.removeEventListener("storage", check);
    };
  }, []);

  // KVKK: kategoriye göre yükle — istatistik (GA4/Yandex), pazarlama (Meta/TikTok/GTM).
  // Consent Mode v2: denied default, kategori açıldıysa granted update.
  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    try {
      window.gtag("consent", "update", {
        analytics_storage: consent.analytics ? "granted" : "denied",
        ad_storage: consent.marketing ? "granted" : "denied",
        ad_user_data: consent.marketing ? "granted" : "denied",
        ad_personalization: consent.marketing ? "granted" : "denied",
      });
    } catch {}
  }, [consent]);

  if (!consent.analytics && !consent.marketing) return null;

  const showAnalytics = consent.analytics;
  const showMarketing = consent.marketing;

  return (
    <>
      {/* Consent default layout'ta beforeInteractive ile yüklenir (client script uyarısı vermemek için) */}
      <RouteTracker gaId={gaId} yandexId={yandexId} />
      {showMarketing && gtmId && (
        <>
          <GoogleTagManager gtmId={gtmId} />
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
              title="Google Tag Manager"
            />
          </noscript>
        </>
      )}
      {showAnalytics && gaId && <GoogleAnalytics gaId={gaId} />}

      {showMarketing && metaId && (
        <>
          <Script id="fb-pixel" strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${metaId}');
              fbq('track', 'PageView');
            `}
          </Script>
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${metaId}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        </>
      )}

      {showMarketing && tiktokId && (
        <>
          <Script id="tiktok-pixel" strategy="afterInteractive">
            {`
              !function (w, d, t) {
                w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
                ttq.load('${tiktokId}');
                ttq.page();
              }(window, document, 'ttq');
            `}
          </Script>
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://analytics.tiktok.com/i18n/pixel/events?sdkid=${tiktokId}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        </>
      )}

      {showAnalytics && yandexId && (
        <>
          <Script id="yandex-metrica" strategy="afterInteractive">
            {`
              (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
              m[i].l=1*new Date();
              for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
              k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
              (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
              ym(${yandexId}, "init", { ssr: true, webvisor: true, clickmap: true, trackLinks: true, accurateTrackBounce: true, ecommerce: "dataLayer", referrer: document.referrer, url: location.href });
            `}
          </Script>
          <noscript>
            <div>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={`https://mc.yandex.ru/watch/${yandexId}`}
                style={{ position: "absolute", left: "-9999px" }}
                alt=""
              />
            </div>
          </noscript>
        </>
      )}
    </>
  );
}
